import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { isEmpty } from 'ts-utils';
import { Experiments, useExperimentSelect } from '../../../helpers/experiments';
import { PricingSelector } from '../../../store/pricing/PricingActions';
import { PurchaseSelector } from '../../../store/purchase/PurchaseActions';
import { useAppStore } from '../../../store/Store';
import { Analytics } from '../../../util/Analytics';
import { trackPaymentAnalyticsEvent } from '../../../util/PaymentAnalytics';
import { useSmoothLoading } from '../../../util/UseSmoothLoading';
import { Checkout } from '../../payment/Checkout';
import { CheckoutInfo, PricingInfoContainer } from '../../payment/CheckoutInfo/CheckoutInfo';
import { CollectCardFormProps } from '../../payment/CollectCardForm';
import { EmailForm } from '../../payment/EmailForm';
import { Header } from '../../payment/Header/Header';
import { PatientAssistanceCta } from '../../payment/PatientAssistanceCta';
import { comboStripeAppearance } from '../../payment/SoftTrialPayment/ComboStripeAppearance';
import { ComboTerms } from '../../payment/SoftTrialPayment/ComboTrust';
import { SoftTrialCombo } from '../../payment/SoftTrialPayment/SoftTrialPayment';
import { Spacer } from '../../styles/Dividers';
import { NextButton } from '../../styles/LayoutButtons';

type Props = Pick<CollectCardFormProps, 'nextPage'>;

export const PaymentLayout: FunctionComponent<Props> = (props) => {
  const { purchase, pricing, user } = useAppStore((state) => ({
    purchase: state.purchase,
    pricing: state.pricing,
    user: state.user,
  }));

  const theme = useTheme();
  // Combo is built around the free trial, so anyone without one keeps their current page and
  // isn't counted as seeing the experiment.
  const hasFreeTrial = PurchaseSelector.hasFreeTrial(purchase);
  // One entry per arm, so a new arm won't compile until it's mapped here.
  const comboArm = useExperimentSelect(
    Experiments.PaymentSoftTrial,
    { Control: false, Combo: true } as const,
    { suppressViewTracking: !hasFreeTrial }
  );
  const isCombo = comboArm && hasFreeTrial;

  const loading = useSmoothLoading(user.loading || purchase.intent.loading || pricing.loading);
  const showEmailForm = useRef(isEmpty(user.email)).current;
  const [showCheckout, setShowCheckout] = useState(
    purchase.payment.loading || purchase.payment.error !== undefined
  );
  // On resume routes (e.g. /start) the page can settle before geolocation has kicked off the first
  // price fetch, so wait for prices rather than tracking an event that has no price data.
  const pricesReady = useAppStore((state) => PricingSelector.prices(state) !== undefined);

  useEffect(
    function trackPaymentOpened() {
      if (!loading && pricesReady) {
        trackPaymentAnalyticsEvent(Analytics.Event.o_payment_opened);
      }
    },
    [loading, pricesReady]
  );

  const purchaseForm = (
    <>
      {showEmailForm ? (
        <EmailForm showButton={showCheckout} setShowCheckout={setShowCheckout} />
      ) : (
        <>
          {showCheckout || (
            <NextButton hideArrow onClick={() => setShowCheckout(true)}>
              Continue
            </NextButton>
          )}
        </>
      )}

      {showCheckout && (
        <Checkout
          nextPage={props.nextPage}
          disclaimer={isCombo ? <ComboTerms /> : undefined}
          stripeAppearance={isCombo ? comboStripeAppearance(theme.font.primary) : undefined}
        />
      )}
    </>
  );

  return (
    <PaymentContainer data-test="payment-screen">
      <Header loading={loading} user={user} hideCoupon={isCombo} />

      {isCombo ? (
        <SoftTrialCombo purchaseForm={purchaseForm} />
      ) : (
        <>
          <CheckoutInfo />
          <PricingInfoContainer>{purchaseForm}</PricingInfoContainer>
        </>
      )}
      <PatientAssistanceCta />
      {/* So the page can scroll a little past the last card. */}
      {isCombo && <Spacer space={48} mobile={48} />}
    </PaymentContainer>
  );
};

const PaymentContainer = styled.div`
  margin: 0 -20px;
  max-width: 600px;
  margin-bottom: ${({ theme }) => theme.spacing._50px};
  display: flex;
  flex-direction: column;

  /* Checkout reads best as a compact column on wide screens. */
  @media (min-width: 600px) {
    max-width: 480px;
    margin-left: auto;
    margin-right: auto;
  }
`;
